import { FunctionComponent, useEffect } from 'react';

import TrophyIcon from '@/components/icons/Trophy';

interface ConfettiOptions {
  spread: number;
  startVelocity?: number;
  decay?: number;
  scalar?: number;
  origin?: {
    x?: number;
    y?: number;
  };
  particleCount?: number;
}

declare global {
  interface Window {
    confetti: (options: ConfettiOptions) => void;
  }
}

interface AwardProps extends RewindSlideProps {
  award: string;
}

const Award: FunctionComponent<AwardProps> = (props) => {
  const { award, active = false } = props;
  const descriptions: Record<string, string> = {
    Globetrotter: 'Your passport has been stamped with some internationally impeccable tv shows and movies this year, making you one of our top armchair travellers.',
    Cinephile: 'You\'ve movie marathoned like no other this year, taking out our top film buff prize!',
    Tastemaker: 'If there\'s a hot new series streaming, you are the first to binge.',
    'True Detective': 'You know your Nordic Noir from your Canadian Crime, making you SBS On Demand\'s top mystery solver of 2023.',
    'Knowledge Is Power': 'You\'ve kept it real this year, with some award-worthy factual viewing that has engaged, challenged and opened your eyes to the world around you.',
    'Chef\'s Kiss': 'Can we come to your place for dinner? As one of our top foodie fans, we have to say, you have exceptional taste.',
    'Star Athlete': 'You came to play and you gave it 110%. Take a victory lap! You\'re one of the year’s top sport streamers.',
    'Comedy Club': 'Laugh your way to the podium. You\'ve found the funny side of things this year.',
  };

  useEffect(() => {
    if (active) {
      const timeoutHandler = setTimeout(() => {
        const count = 200;
        const defaults = {
          origin: { y: 0.7 },
        };

        const fire = (particleRatio: number, opts: ConfettiOptions) => {
          window.confetti({
            ...defaults,
            ...opts,
            origin: { y: 0.4 },
            particleCount: Math.floor(count * particleRatio),
          });
        };

        fire(0.25, {
          spread: 104,
          startVelocity: 55,
        });

        fire(0.2, {
          spread: 240,
        });

        fire(0.35, {
          spread: 400,
          decay: 0.91,
          scalar: 0.8,
        });

        fire(0.1, {
          spread: 480,
          startVelocity: 25,
          decay: 0.92,
          scalar: 1.2,
        });

        fire(0.1, {
          spread: 480,
          startVelocity: 45,
        });
      }, 900);
      return () => {
        clearTimeout(timeoutHandler);
      };
    }

    return () => { };
  }, [active]);

  return (
    <div className="pt-2 xs:px-2 sm:px-10 lg:px-20 lg:px-40 flex flex-col lg:gap-40 lg:flex-row items-center lg:w-[1024px] 2xl:w-[1440px]">
      <div className="grow">
        <div className="h-full flex flex-col text-center lg:text-left max-w-[700px] lg:max-w-[620px]">
          <h2 className="text-xl sm:text-2xl lg:text-3xl 2xl:text-4xl slide-up delay-300">
            And you&apos;ve won the
            {' '}
            {award}
            {' '}
            Award.
            {' '}
            <span className="text-dark-tangerine">Congratulations!</span>
          </h2>
          <p className="text-base sm:text-lg mt-4 sm:mt-10 slide-down delay-[600ms]">{descriptions[award]}</p>
        </div>
      </div>
      <div className="mt-[10px] xs:mt-[70px] sm:mt-[100px] lg:mt-0 slide-in-from-right">
        <TrophyIcon className="w-[200px] lg:w-[380px] xl:w-[404px]"/>
      </div>
    </div>
  );
};

export default Award;
